import CustomLoadingButton from 'Components/CustomLoadingButton'
import FormTextField from 'Components/FormTextField'
import InfoTooltip from 'Components/InfoTooltip'
import StyledForm from 'Components/StyledForm'
import StyledLabel from 'Components/StyledLabel'
import TranslationInput from 'Components/TranslationInput'
import { WithLoader } from 'Components/WithLoader'
import {
  getPtaQueryParamOrSkip,
  useGetAllowedTokensParameterGroupQuery,
  useGetCustomerGroupParameterGroupQuery,
  useGetProductGroupParameterGroupQuery
} from 'Services/ProductManagement/Features/Parameter/parameterApi'
import { AllowedActivationMethodsInfoTooltip } from 'Services/ProductManagement/Features/Product/Components/AllowedActivationMethodsInfoTooltip'
import { SELECTED_PRODUCT_PARAMETER_KEY } from 'Services/ProductManagement/Features/Product/Hooks/useProductsWithParams'
import {
  useCreateSingleProductMutation,
  useUpdateSingleProductMutation
} from 'Services/ProductManagement/Features/Product/productApi'
import {
  AllowedActivationMethods,
  DurationUnit,
  type Product,
  ProductUsages
} from 'Services/ProductManagement/Features/Product/types'
import type { VATDefinition } from 'Services/ProductManagement/Features/VATDefinition/types'
import { type Translation, useTypedSelector } from 'Store'
import type { ProductTariffError } from 'Util/ApiClient'
import {
  DEFAULT_VAT_DEFINITIONS,
  FORM_FIELD_DEFAULT_MIN_WIDTH,
  IS_HSL_SERVICE_ENVIRONMENT,
  IS_WALTTI_SERVICE_ENVIRONMENT
} from 'Util/constants'
import { calculateISODurationLength, createDurationMap } from 'Util/formatters'
import getTranslation from 'Util/getTranslation'
import { useValidationErrors } from 'Util/hooks/useValidationErrors'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamsToUrl } from 'Util/url'
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { c, t } from 'ttag'

const fieldErrorsDefaultState: Record<string, string[]> = {
  activation_valid_days: [],
  product_code: [],
  product_group: [],
  duration: [],
  usage_validity_duration: [],
  usage_trigger_event: [],
  max_purchase_advance_duration: [],
  customer_group: [],
  allowed_tokens: [],
  vat_code: [],
  translations: [],
  shared_usage: [],
  strong_identification_required: [],
  print_zero_price: [],
  max_trips_per_day: [],
  max_device_transfers: [],
  max_days_per_product: [],
  max_trips_per_product: []
}

interface ProductFormProps {
  product?: Product
  successBaseUrl: string
  vatDefinition?: VATDefinition | null
}

export default function ProductForm({
  product,
  successBaseUrl,
  vatDefinition
}: Readonly<ProductFormProps>) {
  const navigate = useNavigate()
  const isEdit = !!product
  const { user } = useTypedSelector(state => state.user)

  const [createSingleProduct, createStatus] = useCreateSingleProductMutation()
  const [updateSingleProduct, updateStatus] = useUpdateSingleProductMutation()

  const mutationStatus = isEdit ? updateStatus : createStatus
  const { isLoading: isSubmitLoading } = mutationStatus

  const {
    data: productGroupParameterGroup = null,
    isLoading: isProductGroupParameterGroupLoading
  } = useGetProductGroupParameterGroupQuery(getPtaQueryParamOrSkip(user?.pta), {
    refetchOnMountOrArgChange: true
  })

  const {
    data: customerGroupParameterGroup = null,
    isLoading: isCustomerGroupParameterGroupLoading
  } = useGetCustomerGroupParameterGroupQuery(getPtaQueryParamOrSkip(user?.pta), {
    refetchOnMountOrArgChange: true
  })

  const {
    data: allowedTokensParameterGroup = null,
    isLoading: isAllowedTokensParameterGroupLoading
  } = useGetAllowedTokensParameterGroupQuery(getPtaQueryParamOrSkip(user?.pta), {
    refetchOnMountOrArgChange: true
  })

  const [productDetails, setProductDetails] = useState<Partial<Product>>(
    (isEdit && product) || {
      activation_window: { duration: null, period: [] }
    }
  )
  const { fieldErrors, setValidationErrors, getFieldErrorHelperText, getErrorProps } =
    useValidationErrors(fieldErrorsDefaultState)
  const [validityFormat, setValidityFormat] = useState(DurationUnit.DAYS)
  const [validityLength, setValidityLength] = useState<number>()
  const [maxPurchaseAdvanceDurationFormat, setMaxPurchaseAdvanceDurationFormat] = useState(
    DurationUnit.DAYS
  )
  const [maxPurchaseAdvanceDuration, setMaxPurchaseAdvanceDuration] = useState<number>()
  const [activityWindowDuration, setActivityWindowDuration] = useState<number>()
  const [activityWindowDurationFormat, setActivityWindowDurationFormat] = useState(
    DurationUnit.DAYS
  )

  const handleProductISODurations = (product: Product) => {
    if (product.usage_validity_duration) {
      const { length, unit } = calculateISODurationLength(product.usage_validity_duration)
      setValidityLength(length)
      setValidityFormat(unit)
    }

    if (product.max_purchase_advance_duration) {
      const { length, unit } = calculateISODurationLength(product.max_purchase_advance_duration)
      setMaxPurchaseAdvanceDuration(length)
      setMaxPurchaseAdvanceDurationFormat(unit)
    }

    if (product.activation_window?.duration) {
      const { length, unit } = calculateISODurationLength(product.activation_window.duration)
      setActivityWindowDuration(length)
      setActivityWindowDurationFormat(unit)
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: suppress handleProductISODurations
  useEffect(() => {
    if (isEdit && product) {
      setProductDetails(product)
      handleProductISODurations(product)
    }
  }, [product])

  const handleChange = (
    value: string | string[] | Translation[] | number | boolean | undefined | null,
    field: keyof Product
  ) => {
    if (value !== undefined) {
      setProductDetails(prevState => ({ ...prevState, [field]: value }) as Product)
    }
  }

  const handleAllowedActivationMethodChange = (value: string | string[], field: keyof Product) => {
    setProductDetails(
      prevState =>
        ({
          ...prevState,
          [field]: value,
          // Nullify the max_purchase_advance_duration if the activation method is not timed.
          // If we don't nullify it manually, the value will remain even though the field is hidden
          max_purchase_advance_duration: !value.includes(AllowedActivationMethods.TIMED)
            ? null
            : prevState.max_purchase_advance_duration,
          activation_window: {
            ...prevState.activation_window,
            duration: !value.includes(AllowedActivationMethods.FIRST_USE)
              ? null
              : prevState.activation_window?.duration
          }
        }) as Product
    )
  }

  const handleSetTranslations = (translations: Translation[]) => {
    handleChange(translations, 'translations')
  }

  const handleValidityFormat = (value: DurationUnit) => {
    setValidityFormat(value)

    if (validityLength) {
      const durationMap = createDurationMap(validityLength)
      handleChange(durationMap[value], 'usage_validity_duration')
    }
  }

  const handleValidityLength = (value: number) => {
    setValidityLength(value)

    const durationMap = createDurationMap(value)
    handleChange(durationMap[validityFormat], 'usage_validity_duration')
  }

  const handleMaxPurchaseAdvanceDuration = (value: number) => {
    setMaxPurchaseAdvanceDuration(value)

    const durationMap = createDurationMap(value)
    handleChange(durationMap[maxPurchaseAdvanceDurationFormat], 'max_purchase_advance_duration')
  }

  const handleMaxPurchaseAdvanceDurationFormat = (value: DurationUnit) => {
    setMaxPurchaseAdvanceDurationFormat(value)

    if (maxPurchaseAdvanceDuration) {
      const durationMap = createDurationMap(maxPurchaseAdvanceDuration)
      handleChange(durationMap[value], 'max_purchase_advance_duration')
    }
  }

  const handleActivationWindowDuration = (value: number) => {
    setActivityWindowDuration(value)

    const durationMap = createDurationMap(value)
    setProductDetails(
      prevState =>
        ({
          ...prevState,
          activation_window: {
            ...prevState.activation_window,
            duration: durationMap[activityWindowDurationFormat]
          }
        }) as Product
    )
  }

  const handleActivationWindowDurationFormat = (value: DurationUnit) => {
    setActivityWindowDurationFormat(value)

    if (activityWindowDuration) {
      const durationMap = createDurationMap(activityWindowDuration)
      setProductDetails(
        prevState =>
          ({
            ...prevState,
            activation_window: {
              ...prevState.activation_window,
              duration: durationMap[value]
            }
          }) as Product
      )
    }
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const productData = {
      ...productDetails
    }

    const mutation = isEdit
      ? updateSingleProduct({ ...productData, id: product.id })
      : createSingleProduct(productData)

    mutation
      .unwrap()
      .then((productResponse: Product) => {
        SnackbarUtils.success(t`Product successfully saved.`)
        navigate(
          appendQueryParamsToUrl(successBaseUrl, {
            [SELECTED_PRODUCT_PARAMETER_KEY]: productResponse.id
          })
        )
      })
      .catch(({ data: errorResponse }: { data: ProductTariffError }) => {
        const { violations } = errorResponse
        setValidationErrors(violations)
        SnackbarUtils.error(t`There was an error saving the product.`)
      })
  }

  const productGroupOptions =
    productGroupParameterGroup?.parameters.map(({ translations, parameter_id }) => ({
      label: getTranslation(translations, 'fi').value ?? '',
      value: parameter_id
    })) ?? []

  const customerGroupOptions =
    customerGroupParameterGroup?.parameters.map(({ translations, parameter_id }) => ({
      label: getTranslation(translations, 'fi').value ?? '',
      value: parameter_id
    })) ?? []
  // Add empty option for customer group
  customerGroupOptions.unshift({
    label: '-',
    value: ''
  })

  const allowedTokenOptions =
    allowedTokensParameterGroup?.parameters.map(({ translations, parameter_id }) => ({
      label: getTranslation(translations, 'fi').value ?? '',
      value: parameter_id
    })) ?? []

  const vatDefinitionOptions =
    vatDefinition?.vat_percentages?.map(({ vat_code, vat_percent }) => ({
      label: `${vat_code} (${vat_percent} %)`,
      value: vat_code
    })) ?? DEFAULT_VAT_DEFINITIONS

  const allowedActivationMethodOptions = [
    {
      label: t`Purchase`,
      value: AllowedActivationMethods.PURCHASE
    },
    {
      label: t`First use`,
      value: AllowedActivationMethods.FIRST_USE
    },
    {
      label: t`Timed`,
      value: AllowedActivationMethods.TIMED
    }
  ]

  const sharedUsageOptions = [
    {
      label: t`Single user`,
      value: ProductUsages.SINGLE_USER
    },
    {
      label: t`Concurrent users`,
      value: ProductUsages.CONCURRENT_USERS
    }
  ]

  const isLoading =
    isProductGroupParameterGroupLoading ||
    isCustomerGroupParameterGroupLoading ||
    isAllowedTokensParameterGroupLoading

  return (
    <WithLoader loading={isLoading}>
      <Grid container spacing={2} mb={4}>
        <Grid size={12}>
          <StyledForm onSubmit={handleSubmit} isDisabled={isSubmitLoading}>
            {/* PRODUCT CODE AND GROUP BOF */}
            <Box sx={{ display: 'flex', gap: 3 }}>
              <FormTextField
                name="product_code"
                required
                label={t`Product code`}
                value={productDetails?.product_code}
                onChange={e => handleChange(e.target.value, 'product_code')}
                {...getErrorProps('product_code')}
              />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControl required>
                  <InputLabel size="small">{t`Product group`}</InputLabel>
                  <Select
                    name="product_group"
                    required
                    size="small"
                    label={t`Product group`}
                    value={productDetails?.product_group}
                    error={fieldErrors.product_group.length > 0}
                    onChange={e => handleChange(e.target.value, 'product_group')}
                    sx={{ minWidth: FORM_FIELD_DEFAULT_MIN_WIDTH }}
                  >
                    {productGroupOptions.map(({ label, value }) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error={fieldErrors.product_group.length > 0}>
                    {getFieldErrorHelperText('product_group')}
                  </FormHelperText>
                </FormControl>
                <Box sx={{ p: 1 }}>
                  <InfoTooltip
                    title={
                      <Typography variant="body2">
                        {t`Product type affects the sales rules used for the product. Available product
                  types are managed in the product parameters section.`}
                      </Typography>
                    }
                  />
                </Box>
              </Box>
            </Box>
            {/* PRODUCT CODE AND GROUP EOF */}

            {/* TRANSLATIONS BOF */}
            <Box>
              <StyledLabel>{t`Product name and translations`}</StyledLabel>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography fontSize={18} sx={{ mr: 1, minWidth: 30 }} color="text.secondary">
                    FI
                  </Typography>
                  <TranslationInput
                    translations={productDetails?.translations ?? []}
                    field="name"
                    label={t`Name`}
                    locale={'fi'}
                    setTranslations={handleSetTranslations}
                    size="small"
                  />
                  <TranslationInput
                    translations={productDetails?.translations ?? []}
                    field="short_name"
                    label={t`Short name`}
                    locale={'fi'}
                    required={false}
                    setTranslations={handleSetTranslations}
                    size="small"
                    sx={{ ml: 3 }}
                  />
                  <TranslationInput
                    translations={productDetails?.translations ?? []}
                    field="description"
                    label={t`Description`}
                    locale={'fi'}
                    required={false}
                    setTranslations={handleSetTranslations}
                    size="small"
                    sx={{ ml: 3 }}
                  />
                  <TranslationInput
                    translations={productDetails?.translations ?? []}
                    field="additional_name"
                    label={t`Additional name`}
                    locale={'fi'}
                    required={false}
                    setTranslations={handleSetTranslations}
                    size="small"
                    sx={{ ml: 3 }}
                  />
                </Box>
              </Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography fontSize={18} sx={{ mr: 1, minWidth: 30 }} color="text.secondary">
                    SV
                  </Typography>
                  <TranslationInput
                    translations={productDetails?.translations ?? []}
                    field="name"
                    label={t`Name`}
                    locale={'sv'}
                    setTranslations={handleSetTranslations}
                    size="small"
                  />
                  <TranslationInput
                    translations={productDetails?.translations ?? []}
                    field="short_name"
                    label={t`Short name`}
                    locale={'sv'}
                    required={false}
                    setTranslations={handleSetTranslations}
                    size="small"
                    sx={{ ml: 3 }}
                  />
                  <TranslationInput
                    translations={productDetails?.translations ?? []}
                    field="description"
                    label={t`Description`}
                    locale={'sv'}
                    required={false}
                    setTranslations={handleSetTranslations}
                    size="small"
                    sx={{ ml: 3 }}
                  />
                  <TranslationInput
                    translations={productDetails?.translations ?? []}
                    field="additional_name"
                    label={t`Additional name`}
                    locale={'sv'}
                    required={false}
                    setTranslations={handleSetTranslations}
                    size="small"
                    sx={{ ml: 3 }}
                  />
                </Box>
              </Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography fontSize={18} sx={{ mr: 1, minWidth: 30 }} color="text.secondary">
                    EN
                  </Typography>
                  <TranslationInput
                    translations={productDetails?.translations ?? []}
                    field="name"
                    label={t`Name`}
                    locale={'en'}
                    setTranslations={handleSetTranslations}
                    size="small"
                  />
                  <TranslationInput
                    translations={productDetails?.translations ?? []}
                    field="short_name"
                    label={t`Short name`}
                    locale={'en'}
                    setTranslations={handleSetTranslations}
                    size="small"
                    required={false}
                    sx={{ ml: 3 }}
                  />
                  <TranslationInput
                    translations={productDetails?.translations ?? []}
                    field="description"
                    label={t`Description`}
                    locale={'en'}
                    required={false}
                    setTranslations={handleSetTranslations}
                    size="small"
                    sx={{ ml: 3 }}
                  />
                  <TranslationInput
                    translations={productDetails?.translations ?? []}
                    field="additional_name"
                    label={t`Additional name`}
                    locale={'en'}
                    required={false}
                    setTranslations={handleSetTranslations}
                    size="small"
                    sx={{ ml: 3 }}
                  />
                </Box>
              </Box>
            </Box>
            {/* TRANSLATIONS EOF */}

            {/* VALIDITY BOF */}
            <Box sx={{ my: 4 }}>
              <StyledLabel>{t`Product validity`}</StyledLabel>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <FormTextField
                  name="usage_validity_duration"
                  required
                  label={t`Usage validity duration`}
                  type="number"
                  value={validityLength}
                  onChange={e => handleValidityLength(Number.parseInt(e.target.value, 10))}
                  slotProps={{ htmlInput: { min: 0 } }}
                  {...getErrorProps('usage_validity_duration')}
                />
                <FormControl sx={{ minWidth: 120 }} required>
                  <InputLabel size="small">{t`Time unit`}</InputLabel>
                  <Select
                    name="validity_format"
                    required
                    size="small"
                    label={t`Validity format`}
                    value={validityFormat ?? ''}
                    onChange={e => {
                      handleValidityFormat(e.target.value as DurationUnit)
                    }}
                  >
                    <MenuItem value={DurationUnit.DAYS}>{c('unit').t`d`}</MenuItem>
                    <MenuItem value={DurationUnit.HOURS}>{c('unit').t`h`}</MenuItem>
                    <MenuItem value={DurationUnit.MINUTES}>{c('unit').t`min`}</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControl sx={{ minWidth: FORM_FIELD_DEFAULT_MIN_WIDTH }} required>
                    <InputLabel size="small">{t`Allowed activation method`}</InputLabel>
                    <Select
                      name="validity_begins_from"
                      required
                      multiple
                      size="small"
                      label={t`Allowed activation method`}
                      value={productDetails?.usage_trigger_event ?? []}
                      error={fieldErrors.usage_trigger_event?.length > 0}
                      onChange={e =>
                        handleAllowedActivationMethodChange(e.target.value, 'usage_trigger_event')
                      }
                    >
                      {allowedActivationMethodOptions.map(({ label, value }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText sx={{ mt: 0 }}>{t`You can select multiple`}</FormHelperText>
                    <FormHelperText error={fieldErrors.usage_trigger_event?.length > 0}>
                      {getFieldErrorHelperText('usage_trigger_event')}
                    </FormHelperText>
                  </FormControl>
                  <Box sx={{ mb: 3 }}>
                    <AllowedActivationMethodsInfoTooltip />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {productDetails?.usage_trigger_event?.includes(
                    AllowedActivationMethods.TIMED
                  ) && (
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <FormTextField
                        name="max_purchase_advance_duration"
                        required
                        label={t`Purchase in advance max`}
                        type="number"
                        value={maxPurchaseAdvanceDuration}
                        onChange={e =>
                          handleMaxPurchaseAdvanceDuration(Number.parseInt(e.target.value, 10))
                        }
                        slotProps={{ htmlInput: { min: 0 } }}
                        {...getErrorProps('max_purchase_advance_duration')}
                      />
                      <FormControl sx={{ minWidth: 120 }} required>
                        <InputLabel size="small">{t`Time unit`}</InputLabel>
                        <Select
                          name="max_purchase_advance_duration_format"
                          required
                          size="small"
                          label={t`Validity format`}
                          value={maxPurchaseAdvanceDurationFormat ?? ''}
                          onChange={e => {
                            handleMaxPurchaseAdvanceDurationFormat(e.target.value as DurationUnit)
                          }}
                        >
                          <MenuItem value={DurationUnit.DAYS}>{c('unit').t`d`}</MenuItem>
                          <MenuItem value={DurationUnit.HOURS}>{c('unit').t`h`}</MenuItem>
                          <MenuItem value={DurationUnit.MINUTES}>{c('unit').t`min`}</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                  {productDetails?.usage_trigger_event?.includes(
                    AllowedActivationMethods.FIRST_USE
                  ) && (
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <FormTextField
                        name="duration"
                        required
                        label={t`Activation window duration`}
                        type="number"
                        value={activityWindowDuration}
                        slotProps={{ htmlInput: { min: 0 } }}
                        onChange={e =>
                          handleActivationWindowDuration(
                            Math.max(0, Number.parseInt(e.target.value, 10))
                          )
                        }
                        {...getErrorProps('duration')}
                      />
                      <FormControl sx={{ minWidth: 120 }} required>
                        <InputLabel size="small">{t`Time unit`}</InputLabel>
                        <Select
                          name="duration_format"
                          required
                          size="small"
                          label={t`Validity format`}
                          value={activityWindowDurationFormat ?? ''}
                          onChange={e => {
                            handleActivationWindowDurationFormat(e.target.value as DurationUnit)
                          }}
                        >
                          <MenuItem value={DurationUnit.DAYS}>{c('unit').t`d`}</MenuItem>
                          <MenuItem value={DurationUnit.HOURS}>{c('unit').t`h`}</MenuItem>
                          <MenuItem value={DurationUnit.MINUTES}>{c('unit').t`min`}</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
            {/* VALIDITY EOF */}

            {/* AVAILABILITY AND USAGE BOF */}
            <Box>
              <StyledLabel>{t`Product availability and usage`}</StyledLabel>
              <Box>
                <FormControl sx={{ minWidth: FORM_FIELD_DEFAULT_MIN_WIDTH }}>
                  <InputLabel size="small">{t`Customer group`}</InputLabel>
                  <Select
                    name="customer_group"
                    size="small"
                    label={t`Customer group`}
                    value={productDetails?.customer_group ?? ''}
                    error={fieldErrors.customer_group?.length > 0}
                    onChange={e => handleChange(e.target.value, 'customer_group')}
                  >
                    {customerGroupOptions.map(({ label, value }) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error={fieldErrors.customer_group?.length > 0}>
                    {getFieldErrorHelperText('customer_group')}
                  </FormHelperText>
                </FormControl>
              </Box>
              <Box>
                <FormControl sx={{ minWidth: FORM_FIELD_DEFAULT_MIN_WIDTH }} required>
                  <InputLabel size="small">{t`Allowed tokens`}</InputLabel>
                  <Select
                    name="allowed_tokens"
                    required
                    multiple
                    size="small"
                    label={t`Allowed tokens`}
                    value={productDetails?.allowed_tokens ?? []}
                    error={fieldErrors.allowed_tokens?.length > 0}
                    onChange={e => handleChange(e.target.value, 'allowed_tokens')}
                  >
                    {allowedTokenOptions.map(({ label, value }) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText sx={{ mt: 0 }}>{t`You can select multiple`}</FormHelperText>
                  <FormHelperText error={fieldErrors.allowed_tokens?.length > 0}>
                    {getFieldErrorHelperText('allowed_tokens')}
                  </FormHelperText>
                </FormControl>
              </Box>
              <Box>
                <FormControl sx={{ minWidth: FORM_FIELD_DEFAULT_MIN_WIDTH }} required>
                  <InputLabel size="small">{t`Value added tax`}</InputLabel>
                  <Select
                    name="vat_code"
                    required
                    size="small"
                    label={t`Value added tax`}
                    value={productDetails?.vat_code ?? ''}
                    error={fieldErrors.vat_code?.length > 0}
                    onChange={e => handleChange(e.target.value, 'vat_code')}
                  >
                    {vatDefinitionOptions.map(({ label, value }) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error={fieldErrors.vat_code?.length > 0}>
                    {getFieldErrorHelperText('vat_code')}
                  </FormHelperText>
                </FormControl>
              </Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControl sx={{ minWidth: FORM_FIELD_DEFAULT_MIN_WIDTH }} required>
                    <InputLabel size="small">{t`Shared usage`}</InputLabel>
                    <Select
                      name="shared_usage"
                      required
                      size="small"
                      label={t`Shared usage`}
                      value={productDetails?.shared_usage ?? ''}
                      error={fieldErrors.shared_usage?.length > 0}
                      onChange={e => handleChange(e.target.value, 'shared_usage')}
                    >
                      {sharedUsageOptions.map(({ label, value }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText error={fieldErrors.shared_usage?.length > 0}>
                      {getFieldErrorHelperText('shared_usage')}
                    </FormHelperText>
                  </FormControl>
                  <Box sx={{ p: 1 }}>
                    <InfoTooltip
                      title={
                        <Typography variant="body2">
                          {t`The value determines whether the product can be used only by the travel account holder or also by fellow passengers.`}
                        </Typography>
                      }
                    />
                  </Box>
                </Box>
              </Box>
              {IS_WALTTI_SERVICE_ENVIRONMENT && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormTextField
                      name="max_trips_day"
                      label={t`Maximum trips per day`}
                      type="number"
                      value={productDetails?.max_trips_per_day ?? ''}
                      onChange={e =>
                        handleChange(
                          e.target.value === '' ? null : Number.parseInt(e.target.value, 10),
                          'max_trips_per_day'
                        )
                      }
                      {...getErrorProps('max_trips_per_day')}
                    />
                    <Box sx={{ p: 1 }}>
                      <InfoTooltip
                        title={
                          <Typography variant="body2">
                            {t`How many daily trips are included in the ticket. A transfer within the transfer time limit does not count as a trip.`}
                          </Typography>
                        }
                      />
                    </Box>
                  </Box>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <FormTextField
                        name="max_device_transfers"
                        label={t`Maximum device transfers`}
                        type="number"
                        value={productDetails?.max_device_transfers ?? ''}
                        onChange={e =>
                          handleChange(
                            e.target.value === '' ? null : Number.parseInt(e.target.value, 10),
                            'max_device_transfers'
                          )
                        }
                        {...getErrorProps('max_device_transfers')}
                      />
                      <Box sx={{ p: 1 }}>
                        <InfoTooltip
                          title={
                            <Typography variant="body2">
                              {t`How many times the ticket can be transferred from one phone to another.`}
                            </Typography>
                          }
                        />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <FormTextField
                        name="max_days_per_product"
                        label={t`Maximum days per product`}
                        type="number"
                        value={productDetails?.max_days_per_product ?? ''}
                        onChange={e =>
                          handleChange(
                            e.target.value === '' ? null : Number.parseInt(e.target.value, 10),
                            'max_days_per_product'
                          )
                        }
                        {...getErrorProps('max_days_per_product')}
                      />
                      <Box sx={{ p: 1 }}>
                        <InfoTooltip
                          title={
                            <Typography variant="body2">
                              {t`How many individual travel days are included in the ticket.`}
                            </Typography>
                          }
                        />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <FormTextField
                        name="max_trips_per_product"
                        label={t`Maximum trips per product`}
                        type="number"
                        value={productDetails?.max_trips_per_product ?? ''}
                        onChange={e =>
                          handleChange(
                            e.target.value === '' ? null : Number.parseInt(e.target.value, 10),
                            'max_trips_per_product'
                          )
                        }
                        {...getErrorProps('max_trips_per_product')}
                      />
                      <Box sx={{ p: 1 }}>
                        <InfoTooltip
                          title={
                            <Typography variant="body2">
                              {t`How many individual trips are included in the ticket.`}
                            </Typography>
                          }
                        />
                      </Box>
                    </Box>
                  </Box>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={productDetails.print_zero_price}
                          onChange={e => handleChange(e.target.checked, 'print_zero_price')}
                          name="print_zero_price"
                          color="primary"
                        />
                      }
                      label={t`Print zero price`}
                    />
                    <FormHelperText error={fieldErrors.print_zero_price?.length > 0}>
                      {getFieldErrorHelperText('print_zero_price')}
                    </FormHelperText>
                  </FormGroup>
                </Box>
              )}
              {IS_HSL_SERVICE_ENVIRONMENT && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <FormTextField
                    name="max_trips_per_product"
                    label={t`Maximum trips per product`}
                    type="number"
                    value={productDetails?.max_trips_per_product ?? ''}
                    onChange={e =>
                      handleChange(
                        e.target.value === '' ? null : Number.parseInt(e.target.value, 10),
                        'max_trips_per_product'
                      )
                    }
                    {...getErrorProps('max_trips_per_product')}
                  />
                  <Box sx={{ p: 1 }}>
                    <InfoTooltip
                      title={
                        <Typography variant="body2">
                          {t`How many individual trips are included in the ticket.`}
                        </Typography>
                      }
                    />
                  </Box>
                </Box>
              )}
              <Box>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={productDetails.strong_identification_required}
                        onChange={e =>
                          handleChange(e.target.checked, 'strong_identification_required')
                        }
                        name="strong_identification_required"
                        color="primary"
                      />
                    }
                    label={t`Strong identification required`}
                  />
                  <FormHelperText error={fieldErrors.strong_identification_required?.length > 0}>
                    {getFieldErrorHelperText('strong_identification_required')}
                  </FormHelperText>
                </FormGroup>
              </Box>
            </Box>
            {/* AVAILABILITY AND USAGE EOF */}
            <CustomLoadingButton
              isLoading={isSubmitLoading}
              type="submit"
              size="large"
              iconPosition="end"
            >
              {t`Save product`}
            </CustomLoadingButton>
          </StyledForm>
        </Grid>
      </Grid>
    </WithLoader>
  )
}
